import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { HiMiniHome } from "react-icons/hi2";
import ButtonLoader from "../../components/common/ButtonLoader";
import bgImage from "../../Assets/Images/loginBg.webp"; // full-screen background
import illustration from "../../Assets/Images/loginImg.webp";
import { FiCheckCircle, FiLogIn } from "react-icons/fi";
import toast from "react-hot-toast";
import { FiAlertTriangle } from "react-icons/fi";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    phone: "",
    newPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", phone: "", newPassword: "" };

    if (!email) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address.";
      valid = false;
    }

    if (!phone) {
      newErrors.phone = "Phone is required.";
      valid = false;
    }
    if (!newPassword) {
      newErrors.newPassword = "Password is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setApiError("");

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admins/admin-forgot-password`,
        { email, phoneNumber: phone, newPassword }
      );
      toast("Password changed successfully ", {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: "linear-gradient(135deg, #ff1744, #ff5252, #ff8a80)",
          // background: "linear-gradient(135deg, #00C853, #1DE9B6, #76FF03)",
          color: "#fff",
          fontWeight: "600",
          fontFamily: "'Poppins', sans-serif",
          borderRadius: "14px",
          border: "1px solid rgba(0, 230, 118, 0.5)",
        },
        icon: <FiCheckCircle size={22} color="#fff" />,
      });
      navigate("/admin-login");
      setEmail("");
      setNewPassword("");
      setPhone("");
    } catch (error) {
      toast(
        error.response?.data?.message || "Something went wrong. Try again.",
        {
          position: "top-right",
          autoClose: 5000,
          style: {
            background: "linear-gradient(135deg, #ff4d6d, #ff1e40)",
            color: "#fff",
            fontWeight: "500",
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "12px",
            border: "1px solid rgba(255, 77, 109, 0.4)",
            boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
          },
          icon: <FiAlertTriangle size={36} color="#fff" />,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60"></div>

      <div className="relative z-10 flex flex-col lg:flex-row w-full max-w-6xl rounded-3xl overflow-hidden shadow-2xl bg-white/90 backdrop-blur-md">
        {/* Left Side Illustration */}
        <div
          className="hidden lg:block lg:w-1/2 bg-cover bg-center"
          style={{ backgroundImage: `url(${illustration})` }}
        ></div>

        {/* ForgotPassword Form */}
        <div className="w-full lg:w-1/2 p-10 relative">
          {/* Decorative Circles */}
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full opacity-20 animate-pulse"></div>

          <h1 className="text-4xl font-bold text-primaryBtnColor mb-2 text-center lg:text-left">
            Strikes & Shots
          </h1>
          <p className="text-center lg:text-left text-gray-700 mb-8">
            Enter your credentials to continue
          </p>

          {apiError && (
            <p className="text-center text-red-500 mb-4 font-medium">
              {apiError}
            </p>
          )}

          <div className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primaryBtnColor"
                }`}
                placeholder="admin@gmail.com"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 ${
                  errors.phone
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primaryBtnColor"
                }`}
                placeholder="+91 9876543210"
              />
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>

            {/*New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 ${
                  errors.newPassword
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primaryBtnColor"
                }`}
                placeholder="Enter your new password"
              />
              {errors.newPassword && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* ForgotPassword Button */}
            <div>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full h-12 flex justify-center items-center bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition duration-300"
              >
                {isLoading ? <ButtonLoader /> : "Change Password"}
              </button>
            </div>

            {/* Navigation Links */}
            <div className="flex justify-between text-sm mt-4 text-gray-700">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-1 hover:text-primaryBtnColor transition"
              >
                <HiMiniHome size={18} /> Home
              </button>
              <button
                onClick={() => navigate("/admin-login")}
                className="flex items-center gap-1 hover:text-primaryBtnColor transition"
              >
                <FiLogIn size={18} /> Remember Password? Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
